import { auth, db, storage } from "../../../firebaseConfig"; // Ensure you have storage imported from your Firebase configuration
import { doc, setDoc, getDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getSelectedUid } from "../../../../frontend/Recruitment/Applications/Utils/UidListener";

// Function to upload files to Firebase Storage
const uploadFileToStorage = async (file) => {
  const userId = getSelectedUid(); // Get the selected UID
  if (!userId) {
    throw new Error("Invalid user.");
  }
  const storageRef = ref(
    storage,
    `uploads/${userId}/ApplicationDetails/AdditionalInformation/${file.name}`
  );
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

// Function to delete files from Firebase Storage
const deleteFileFromStorage = async (fileName) => {
  const userId = getSelectedUid(); // Get the selected UID
  if (!userId) {
    throw new Error("Invalid user.");
  }
  try {
    const fileRef = ref(
      storage,
      `uploads/${userId}/ApplicationDetails/AdditionalInformation/${fileName}`
    );
    await deleteObject(fileRef); // Delete the file from Firebase Storage
  } catch (error) {
    // console.error(`Error deleting file: ${fileName}`, error);
  }
};

// Function to save form data for Section 9, including file uploads and deletions
export const writeDataS9 = async (formData) => {
  const userId = getSelectedUid(); // Get the selected UID
  if (!userId) {
    throw new Error("Invalid user.");
  }
  try {
    // Step 1: Retrieve the previously saved files from Firestore
    const docRef = doc(
      db,
      "Advisers",
      userId,
      "Application Details",
      "Additional Information"
    );
    const docSnap = await getDoc(docRef);
    let previouslySavedFiles = [];

    if (docSnap.exists()) {
      const previousData = docSnap.data();
      previouslySavedFiles = previousData.files || [];
    }

    // Step 2: Find files that are in previouslySavedFiles but not in formData.files (these will be deleted)
    const filesToDelete = [];
    for (const documentKey in previouslySavedFiles) {
      if (
        !formData.files[documentKey] ||
        formData.files[documentKey].length === 0
      ) {
        // If document key is missing in formData.files, mark all files under this key for deletion
        filesToDelete.push(...previouslySavedFiles[documentKey]);
      } else {
        // If document key exists, find individual files to delete
        const currentFiles = formData.files[documentKey].map(
          (file) => file.name
        );
        previouslySavedFiles[documentKey].forEach((file) => {
          if (!currentFiles.includes(file.name)) {
            filesToDelete.push(file);
          }
        });
      }
    }

    // Step 3: Delete the files from Firebase Storage
    await Promise.all(
      filesToDelete.map(async (file) => {
        await deleteFileFromStorage(file.name); // Delete each file from Firebase Storage
      })
    );

    // Step 4: Upload new files to Firebase Storage and get their URLs
    const uploadedFileUrls = {};

    for (const documentKey in formData.files) {
      if (formData.files.hasOwnProperty(documentKey)) {
        uploadedFileUrls[documentKey] = await Promise.all(
          formData.files[documentKey].map(async (file) => {
            if (typeof file === "object") {
              const fileUrl = await uploadFileToStorage(file);
              return { name: file.name, url: fileUrl };
            } else {
              return file; // If already an uploaded file, retain it as is
            }
          })
        );
      }
    }

    const uploadGeneralFiles = await Promise.all(
      formData.generalFiles.map(async (file) => {
        // Only upload new files (not already saved)
        if (typeof file === "object") {
          const fileUrl = await uploadFileToStorage(file);
          return { name: file.name, url: fileUrl };
        } else {
          return file; // Keep already uploaded file URLs
        }
      })
    );

    // Step 5: Prepare the form data with uploaded file URLs
    const dataToSave = {
      ...formData,
      files: uploadedFileUrls, // Replace the files array with file URLs
      generalFiles: uploadGeneralFiles,
    };

    // Step 6: Save the updated form data, including file URLs, to Firestore
    await setDoc(docRef, dataToSave, { merge: true });
    // console.log("Section 9 data successfully written!");
  } catch (error) {
    // console.error("Error writing Section 9 data to Firestore: ", error);
  }
};
